import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import SvgIcon from '@mui/material/SvgIcon';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { NavItemLabel } from './side-nav-shared';
import { navRowSx, toCamelCase } from './side-nav-utils';

/**
 * A nav item with children. Without a `path` the whole row toggles the
 * children; with one (a parent that has its own page, e.g. "apps") the label
 * navigates and only the chevron toggles, so both stay reachable.
 */
export const SideNavBranch = (props) => {
  const { depth, icon, disabled, openProp, offset, active, title, children, path } = props;
  const { t } = useTranslation();
  const [open, setOpen] = useState(!!openProp);
  const handleToggle = useCallback(() => {
    setOpen((prevOpen) => !prevOpen);
  }, []);

  const label = <NavItemLabel depth={depth} icon={icon} active={active} disabled={disabled} title={title} />;
  const toggleProps = {
    onClick: handleToggle,
    disabled,
    'aria-expanded': open,
    'data-testid': `side-nav-branch-toggle-${title}`,
  };
  const chevron = (
    <SvgIcon sx={{ color: 'var(--nav-item-chevron-color)', fontSize: 12, ml: 2 }}>
      <FontAwesomeIcon icon={`fa-regular ${open ? 'fa-angle-down' : 'fa-angle-right'}`} />
    </SvgIcon>
  );

  return (
    <li>
      {path ? (
        <Box sx={{ ...navRowSx({ active, depth, offset }), pl: 0, py: 0 }}>
          <ButtonBase
            component={Link}
            to={path}
            disabled={disabled}
            onClick={() => setOpen(true)}
            data-testid={`side-nav-branch-link-${title}`}
            sx={{ ...navRowSx({ active: false, depth, offset }), flexGrow: 1, width: 'auto', '&:hover': {} }}
          >
            {label}
          </ButtonBase>
          <ButtonBase
            {...toggleProps}
            aria-label={t('admin.toggleSection', { section: t(`admin.${toCamelCase(title)}`) })}
            sx={{ alignItems: 'center', display: 'flex', pr: '16px', py: '6px' }}
          >
            {chevron}
          </ButtonBase>
        </Box>
      ) : (
        <ButtonBase {...toggleProps} sx={{ ...navRowSx({ active, depth, offset }), pr: '16px' }}>
          {label}
          {chevron}
        </ButtonBase>
      )}
      <Collapse in={open} sx={{ mt: 0.5 }}>
        {children}
      </Collapse>
    </li>
  );
};

SideNavBranch.propTypes = {
  depth: PropTypes.number,
  icon: PropTypes.string,
  disabled: PropTypes.bool,
  openProp: PropTypes.bool,
  offset: PropTypes.number,
  active: PropTypes.bool,
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  path: PropTypes.string,
};
